import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import {
  adminService,
  type AdminPageSummary,
  type PageWrite,
} from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import PageBuilder from './PageBuilder'
import NavSettingsSection from './NavSettingsSection'

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

export default function PagesManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [pages, setPages] = useState<AdminPageSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [openPageId, setOpenPageId] = useState<string | null>(null)
  const [settings, setSettings] = useState<{ page: AdminPageSummary | null } | null>(null)

  const load = () =>
    adminService
      .listPages(eventId)
      .then(setPages)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (p: AdminPageSummary) => {
    if (!(await confirmDelete(`Delete page "${p.title}"?`))) return
    try {
      await adminService.deletePage(eventId, p.id)
      notify('Page deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const duplicate = async (p: AdminPageSummary) => {
    try {
      await adminService.duplicatePage(eventId, p.id)
      notify(`Duplicated "${p.title}"`)
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (openPageId) {
    return (
      <PageBuilder
        eventId={eventId}
        pageId={openPageId}
        onBack={() => {
          setOpenPageId(null)
          load()
        }}
      />
    )
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <NavSettingsSection eventId={eventId} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Compose the event site — the home page and any custom pages.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => setSettings({ page: null })}
        >
          New page
        </Button>
      </Stack>

      {pages.length === 0 ? (
        <EmptyState title="No pages yet" description="A home page is created automatically." />
      ) : (
        <Stack spacing={1.5}>
          {pages.map((p) => (
            <Card
              key={p.id}
              variant="outlined"
              sx={{ p: 2, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {p.title}
                  </Typography>
                  {p.isHome && <Chip label="Home" size="small" sx={{ height: 20, fontWeight: 700 }} />}
                  {!p.published && <Chip label="Draft" size="small" color="warning" sx={{ height: 20 }} />}
                  {p.showInNav && !p.isHome && <Chip label="In nav" size="small" variant="outlined" sx={{ height: 20 }} />}
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  /{p.slug}
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={() => setOpenPageId(p.id)}>
                Edit content
              </Button>
              <IconButton size="small" onClick={() => setSettings({ page: p })} title="Settings">
                <TuneRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => duplicate(p)} title="Duplicate">
                <ContentCopyRoundedIcon fontSize="small" />
              </IconButton>
              {!p.isHome && (
                <IconButton size="small" color="error" onClick={() => remove(p)}>
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              )}
            </Card>
          ))}
        </Stack>
      )}

      {settings && (
        <PageSettingsDialog
          eventId={eventId}
          page={settings.page}
          count={pages.length}
          onClose={() => setSettings(null)}
          onSaved={(newId) => {
            setSettings(null)
            load()
            if (newId) setOpenPageId(newId)
          }}
        />
      )}
    </Box>
  )
}

function PageSettingsDialog({
  eventId,
  page,
  count,
  onClose,
  onSaved,
}: {
  eventId: string
  page: AdminPageSummary | null
  count: number
  onClose: () => void
  onSaved: (newId?: string) => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: page?.title ?? '',
    slug: page?.slug ?? '',
    showInNav: page?.showInNav ?? true,
    navLabel: page?.navLabel ?? '',
    published: page?.published ?? true,
  })
  const set = (k: keyof typeof form, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.title) {
      notify('Title is required', 'error')
      return
    }
    const payload: PageWrite = {
      title: form.title,
      showInNav: form.showInNav,
      navLabel: form.navLabel || null,
      published: form.published,
    }
    // Slug only settable on create (home slug is fixed).
    if (!page) payload.slug = form.slug || slugify(form.title)
    setSaving(true)
    try {
      if (page) {
        await adminService.updatePage(eventId, page.id, payload)
        notify('Page updated')
        onSaved()
      } else {
        const created = await adminService.createPage(eventId, {
          ...payload,
          ordinal: count,
        })
        notify('Page created')
        onSaved(created.id)
      }
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{page ? 'Page settings' : 'New page'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
          {!page && (
            <TextField
              label="Slug"
              fullWidth
              value={form.slug}
              placeholder={slugify(form.title) || 'about'}
              onChange={(e) => set('slug', slugify(e.target.value))}
              helperText="URL: /e/{event}/p/{slug}"
            />
          )}
          <TextField
            label="Nav label (optional)"
            fullWidth
            value={form.navLabel}
            onChange={(e) => set('navLabel', e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={form.showInNav} onChange={(e) => set('showInNav', e.target.checked)} />}
            label="Show in attendee navigation"
          />
          <FormControlLabel
            control={<Switch checked={form.published} onChange={(e) => set('published', e.target.checked)} />}
            label="Published"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
